import { useCallback, useEffect, useMemo } from 'react'
import { useIntl } from 'react-intl'
import { rem } from 'polished'
import styled from 'styled-components'
import BodyText from 'LuxKit/Typography/BodyText'
import TextButton from 'LuxKit/Button/TextButton'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import ExperienceCompactSearchTile from '../ExperienceTiles/ExperienceCompactSearchTile'
import ExperienceCompactSearchTileSkeleton from '../ExperienceTiles/ExperienceCompactSearchTileSkeleton'
import { fetchExperiencesById } from 'actions/ExperienceActions'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { setContentTagAttribution } from 'lib/contentTags/contentTagAttribution'

const ListGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(16)};

  ${mediaQueryUp.tablet} {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(${rem(300)}, 1fr));
    gap: ${rem(36)} ${rem(16)};
  }
`

interface Props {
  offerIds: Array<string>
  fetching: boolean
  listFetching?: boolean
  error?: string
  onClear: () => void
  contentTagId?: string
}

function ExperienceSearchContentTagResults({ offerIds, fetching, listFetching, error, onClear, contentTagId }: Props) {
  const intl = useIntl()
  const dispatch = useAppDispatch()
  const experiences = useAppSelector(state => state.experience.experiences)
  const experienceErrors = useAppSelector(state => state.experience.experienceErrors)

  useEffect(() => {
    if (offerIds.length) dispatch(fetchExperiencesById(...offerIds))
  }, [dispatch, offerIds])

  const loaded = useMemo(() => offerIds.map(id => experiences[id]).filter(Boolean), [experiences, offerIds])
  const resolvedCount = useMemo(
    () => offerIds.filter(id => experiences[id] || experienceErrors[id]).length,
    [experienceErrors, experiences, offerIds],
  )
  const isLoading = fetching || listFetching || (offerIds.length > 0 && resolvedCount !== offerIds.length)
  const handleOfferClick = useCallback(() => {
    if (contentTagId) setContentTagAttribution(contentTagId)
  }, [contentTagId])
  const clearLabel = intl.formatMessage({
    id: 'lAeBIFQBqli6ThP7-r-j',
    defaultMessage: 'Clear selected category',
    description: 'Action that clears a selected category filter after no matching experiences were found',
  })

  if (error) {
    return <VerticalSpacer gap={8}>
      <BodyText variant="large">{intl.formatMessage({
        id: 'bxL2P8VoSh0DD18p5XUl',
        defaultMessage: 'We could not load this category. Please try again.',
        description: 'Error message displayed when a selected category result request fails',
      })}
      </BodyText>
      <div><TextButton kind="tertiary" size="small" onClick={onClear}>{clearLabel}</TextButton></div>
    </VerticalSpacer>
  }

  if (!isLoading && offerIds.length === 0) {
    return <VerticalSpacer gap={8}>
      <BodyText variant="large">{intl.formatMessage({
        id: 'mFAO-LSkLLTkS_GF0num',
        defaultMessage: 'No experiences match this category with your current filters.',
        description: 'No-results message for a selected category on experiences search',
      })}
      </BodyText>
      <div><TextButton kind="tertiary" size="small" onClick={onClear}>{clearLabel}</TextButton></div>
    </VerticalSpacer>
  }

  return <ListGrid>
    {isLoading && Array.from({ length: 4 }, (_, index) => <ExperienceCompactSearchTileSkeleton key={index} />)}
    {!isLoading && loaded.map((experience, index) => <ExperienceCompactSearchTile
      key={experience.id}
      experience={experience}
      position={index}
      onOfferClick={contentTagId ? handleOfferClick : undefined}
    />)}
  </ListGrid>
}

export default ExperienceSearchContentTagResults
